import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export default function AuthReset() {
  const [params] = useSearchParams();

  const token = params.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");

  const [status, setStatus] = useState("checking"); // checking | ready | done | error
  const [message, setMessage] = useState("");

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("checking");
    setMessage("");
    setPw1("");
    setPw2("");
    if (!token) {
      setStatus("error");
      setMessage("Lien manquant. Demandez un nouveau lien.");
      return () => controller.abort();
    }
    fetch("/api/v2/auth/reset-password/check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      signal: controller.signal,
    }).then(async (res) => {
      const json = await res.json().catch(() => ({}));
      if (controller.signal.aborted) return;
      setStatus(res.ok && json.ok ? "ready" : "error");
      if (!res.ok || !json.ok) setMessage(json.error || "Impossible de vérifier ce lien pour le moment.");
    }).catch(() => {
      if (controller.signal.aborted) return;
      setStatus("error");
      setMessage("Impossible de vérifier ce lien. Vérifiez votre connexion et rechargez la page.");
    });
    return () => controller.abort();
  }, [token]);

  function validatePasswordLocal(a, b) {
    if (a.length < 6) return "Mot de passe trop court (min 6 caractères).";
    if (!/^[A-Za-z0-9]+$/.test(a)) return "Mot de passe invalide (alphanumérique uniquement).";
    if (a !== b) return "Les deux mots de passe ne correspondent pas.";
    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMessage("");

    if (!token || status !== "ready") {
      setStatus("error");
      setMessage("Lien indisponible. Demandez un nouveau lien.");
      return;
    }

    const err = validatePasswordLocal(pw1, pw2);
    if (err) {
      setStatus("ready");
      setMessage(err);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/v2/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: pw1 }),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        setSubmitting(false);
        setStatus(String(json?.code || "").startsWith("RESET_") ? "error" : "ready");
        setMessage(json?.error || "Impossible de réinitialiser le mot de passe.");
        return;
      }

      setSubmitting(false);
      setStatus("done");
      setMessage(json?.message || "Mot de passe mis à jour.");
    } catch {
      setSubmitting(false);
      setStatus("ready");
      setMessage("Erreur réseau.");
    }
  }

  const S = {
    container: {
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f3f4f6",
      padding: "clamp(24px, 4dvh, 48px) 28px",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    card: {
      width: "clamp(720px, 52vw, 1100px)",
      maxWidth: "calc(100vw - 56px)",
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "24px",
      padding: "clamp(38px, 4vw, 54px)",
      boxShadow: "0 22px 56px rgba(15,23,42,0.12)",
    },
    h1: { margin: 0, fontSize: "40px", fontWeight: 950, color: "#111827" },
    p: { marginTop: "12px", color: "#4b5563", lineHeight: 1.6, fontSize: "16px" },
    label: { fontSize: "15px", fontWeight: 800, color: "#111827" },
    input: {
      width: "100%",
      padding: "15px 14px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      outline: "none",
      fontSize: "16px",
    },
    form: { marginTop: "22px", display: "grid", gap: "16px" },
    row: { display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "14px" },
    btn: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#fff",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    btnLight: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    alertOk: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
      color: "#166534",
      fontSize: "14px",
      lineHeight: 1.5,
    },
    alertErr: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontSize: "14px",
      lineHeight: 1.5,
    },
    hint: { marginTop: "6px", fontSize: "12px", color: "#6b7280" },
  };

  return (
    <div style={S.container}>
      <div style={S.card}>
        <h1 style={S.h1}>Réinitialiser mon mot de passe</h1>

        {status === "checking" ? <p style={S.p}>Vérification du lien…</p> : null}

        {status === "error" ? (
          <div style={S.alertErr} role="alert">
            <b>{message || "Lien invalide."}</b>
            <div style={S.row}>
              <button type="button" style={S.btnLight} onClick={() => { window.location.href = "/auth/forgot"; }}>
                Demander un nouveau lien
              </button>
            </div>
          </div>
        ) : null}

        {status === "ready" ? (
          <>
            <p style={S.p}>
              Définissez un nouveau mot de passe.
            </p>

            <form style={S.form} onSubmit={onSubmit}>
              <div>
                <div style={S.label}>Nouveau mot de passe</div>
                <input
                  style={S.input}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                />
                <div style={S.hint}>Min 6 caractères, alphanumérique uniquement.</div>
              </div>

              <div>
                <div style={S.label}>Confirmer le mot de passe</div>
                <input
                  style={S.input}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                />
              </div>

              <div style={S.row}>
                <button type="submit" style={S.btn} disabled={submitting}>
                  {submitting ? "Validation…" : "Valider"}
                </button>
                <button type="button" style={S.btnLight} onClick={() => { window.location.href = "/auth"; }}>
                  Retour
                </button>
              </div>
            </form>

            {message ? <div style={S.alertErr}><b>{message}</b></div> : null}
          </>
        ) : null}

        {status === "done" ? (
          <div style={S.alertOk}>
            <b>{message || "Mot de passe mis à jour."}</b>
            <div style={{ marginTop: "10px" }}>
              <button type="button" style={S.btn} onClick={() => { window.location.href = "/auth"; }}>
                Continuer
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
